import { create } from 'zustand';

export const createLayoutStore = (initialState) =>
  create((set, get) => ({
    stores: {},
    appState: 'default',
    maxVideoHeight: {
      // the percentages for how tall vs the total session pane the video should take up...
      default: 0.4,
      checkinActive: 0.2,
    },
    registeredEvents: {},
    enableSlidedownVideo: false,
    detailsSplitView: null,
    videoSplitView: null,
    isFullScreen: false,
    containers: {
      session: {
        element: null,
        size: { width: 0, height: 0 },
      },
      video: {
        element: null,
        size: { width: 0, height: 0 },
        height: '400px',
      },
      controls: {
        element: null,
        size: { width: 0, height: 0 },
      },
      playbackProgressBar: {
        element: null,
        size: { width: 0, height: 0 },
      },
      details: {
        element: null,
        size: { width: 0, height: 0 },
      },
      sessionDetails: {
        element: null,
        size: { width: 0, height: 0 },
      },
      header: {
        element: null,
        size: { width: 0, height: 0 },
      },
    },

    injectDependentStores: (stores) => {
      set({ stores });
    },

    storeState: (name) => {
      if (!get().stores[name])
        throw new Error(`${name} is not a valid dependency in this store.`);
      return get().stores[name].getState();
    },

    enterFullScreen: () => {
      const el = get().containers.session.element;

      if (el.requestFullscreen) {
        el.requestFullscreen();
      } else if (el.webkitRequestFullscreen) {
        el.webkitRequestFullscreen();
      } else if (el.mozRequestFullScreen) {
        el.mozRequestFullScreen();
      } else if (el.msRequestFullscreen) {
        el.msRequestFullscreen();
      }
      set({ isFullScreen: true });
    },

    exitFullScreen: () => {
      document.exitFullscreen();
      set({ isFullScreen: false });
    },

    setSessionRef: (ref) => {
      const updatedContainers = { ...get().containers };
      updatedContainers.session.element = ref;
      set({ containers: updatedContainers });
      get().adjustSizing();
    },

    setSessionDetailsHeight: (height) => {
      const updatedContainers = { ...get().containers };
      updatedContainers.sessionDetails.size.height = height;
      set({ containers: updatedContainers });
    },


    setMediaControlsRef: (ref) => {
      // Only update if the ref actually changed to prevent infinite loops
      const currentRef = get().containers.controls.element;
      if (currentRef !== ref) {
        const updatedContainers = { ...get().containers };
        updatedContainers.controls.element = ref;
        set({ containers: updatedContainers });
        // Don't call adjustSizing here to prevent infinite loop
        // Sizing will be adjusted by resize events or other mechanisms
      }
    },

    setHeaderRef: (ref) => {
      // Only update if the ref actually changed to prevent infinite loops
      const currentRef = get().containers.header.element;
      if (currentRef !== ref) {
        const updatedContainers = { ...get().containers };
        updatedContainers.header.element = ref;
        set({ containers: updatedContainers });
        // Don't call adjustSizing here to prevent infinite loop
        // Sizing will be adjusted by resize events or other mechanisms
      }
    },

    setDetailsSplitView: (ref) => {
      set({ detailsSplitView: ref });
    },
    setVideoSplitView: (ref) => {
      set({ videoSplitView: ref });
    },

    setPlaybackProgressBarRef: (ref) => {
      const updatedContainers = { ...get().containers };
      updatedContainers.playbackProgressBar.element = ref;
      set({ containers: updatedContainers });
    },

    initialize: (elements) => {
      const updatedContainers = { ...get().containers };

      updatedContainers.session.element = elements.session;
      updatedContainers.video.element = elements.video;
      updatedContainers.controls.element = elements.controls;
      updatedContainers.header.element = elements.header;
      updatedContainers.details.element = elements.details;

      set({ containers: updatedContainers });
      get().adjustSizing();
    },

    adjustSizing: () => {
      // this is returning back out b/c we don't think we need this...
      return;

      const updatedContainers = { ...get().containers };

      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      const checkinActive = get().storeState('checkinStore').checkinActive;
      let checkinTrayTarget;

      updatedContainers.session.size =
        updatedContainers.session?.element?.getBoundingClientRect() || updatedContainers.session.size;
      updatedContainers.video.size =
        updatedContainers.video?.element?.getBoundingClientRect() || updatedContainers.video.size;
      updatedContainers.controls.size =
        updatedContainers?.controls?.element?.getBoundingClientRect();
      updatedContainers.details.size =
        updatedContainers?.details?.element?.getBoundingClientRect();
      updatedContainers.header.size =
        updatedContainers?.header?.element?.getBoundingClientRect();

      if (checkinActive) {
        // if the vertical height of the session container is not high enough, then we need to make the video even smaller and allow for slide down expansion...

        checkinTrayTarget = 'Controls';

        if (updatedContainers.session.size.height <= 500) {
          updatedContainers.video.height = 200;
        } else {
          updatedContainers.video.height = Math.round(
            updatedContainers.session.size.height *
            get().maxVideoHeight.checkinActive
          );
        }
      } else if (
        get().storeState('appStore').enabledComponents.sessionDetails
      ) {
        // The max height will depend on what controls and mode is being used.
        updatedContainers.video.height = Math.round(
          updatedContainers.session.size.height * get().maxVideoHeight.default
        );
      } else {
        // The max height will depend on what controls and mode is being used.
        updatedContainers.video.height = updatedContainers.session.size.height;
      }

      updatedContainers.video.size.height = updatedContainers.video.height;

      // Go ahead and update the container sizes because we have some other things that need to be sized based on the new locations...
      //set({ containers: updatedContainers });

      // now that we have updated the sizes and evertyhing has moved, we need to set the new sizes of everything into state.

      // give it a slight delay to allow things to properly resize...
      setTimeout(() => {
        // now refresh the sizing values...
        get().refreshSizingValues();

        if (checkinActive) {
          let topTarget;

          topTarget =
            get().containers.playbackProgressBar.element.getBoundingClientRect()
              .bottom - 4;

          let height = get().containers.session.size.height;

          if (get().containers.session.size.height > viewportHeight) {
            height = viewportHeight;
          }

          const trayHeight = height - topTarget;

          get().storeState('checkinStore').setTrayHeight(trayHeight);
        }
      }, 100);
    },

    refreshSizingValues: () => {
      const updatedContainers = { ...get().containers };

      updatedContainers.session.size =
        updatedContainers.session?.element?.getBoundingClientRect() || updatedContainers.session.size;
      updatedContainers.video.size =
        updatedContainers.video?.element?.getBoundingClientRect() || updatedContainers.video.size;
      updatedContainers.controls.size =
        updatedContainers.controls?.element?.getBoundingClientRect();
      updatedContainers.details.size =
        updatedContainers.details?.element?.getBoundingClientRect();
      updatedContainers.header.size =
        updatedContainers.header?.element?.getBoundingClientRect();

      // Go ahead and update the container sizes because we have some other things that need to be sized based on the new locations...
      set({ containers: updatedContainers });
    },
  }));
